import { Controller } from '@hotwired/stimulus';

const FIELD_SELECTOR = 'input, select, textarea';
const SERVER_ERROR_ATTRIBUTE = 'data-server-error';

export default class extends Controller {
  static values = {
    requiredMessage: String,
    emailMessage: String,
  };

  connect () {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.element.setAttribute('novalidate', '');
    this.#rememberServerErrors();
    this.element.addEventListener('focusout', (event) => this.#validateTarget(event.target), { signal });
    this.element.addEventListener('change', (event) => this.#validateTarget(event.target), { signal });
    this.element.addEventListener('input', (event) => this.#clearWhenValid(event.target), { signal });
    this.element.addEventListener('submit', (event) => this.#validateAll(event), { signal });
  }

  disconnect () {
    this.abortController.abort();
  }

  #validateAll (event) {
    const fields = Array.from(this.element.querySelectorAll(FIELD_SELECTOR));
    const invalid = fields.filter((field) => !this.#validate(field));

    if (invalid.length === 0) return;

    event.preventDefault();
    this.#focus(invalid[0]);
  }

  #focus (field) {
    if (field.tomselect) {
      field.tomselect.focus();
      return;
    }

    field.focus();
  }

  #rememberServerErrors () {
    this.element.querySelectorAll(`[${SERVER_ERROR_ATTRIBUTE}]`).forEach((feedback) => {
      const field = feedback.parentElement.querySelector(FIELD_SELECTOR);

      if (!field) return;

      field.dataset.refusedValue = field.value;
      field.dataset.refusedMessage = feedback.textContent.trim();
    });
  }

  #refusedByServer (field) {
    return field.dataset.refusedValue !== undefined && field.dataset.refusedValue === field.value;
  }

  #validateTarget (target) {
    if (!target.matches?.(FIELD_SELECTOR)) return;

    this.#validate(target);
  }

  #clearWhenValid (target) {
    if (!target.matches?.(FIELD_SELECTOR)) return;

    if (target.getAttribute('aria-invalid') === 'true') {
      this.#validate(target);
      return;
    }

    if (!target.checkValidity() || this.#refusedByServer(target)) return;

    this.#markValid(target);
  }

  #validate (field) {
    if (!field.checkValidity()) {
      this.#markInvalid(field, this.#messageFor(field));
      return false;
    }

    if (this.#refusedByServer(field)) {
      this.#markInvalid(field, field.dataset.refusedMessage);
      return false;
    }

    this.#markValid(field);
    return true;
  }

  #messageFor (field) {
    if (field.validity.valueMissing && this.hasRequiredMessageValue) return this.requiredMessageValue;
    if (field.validity.typeMismatch && field.type === 'email' && this.hasEmailMessageValue) {
      return this.emailMessageValue;
    }

    return field.validationMessage;
  }

  #markInvalid (field, message) {
    const feedback = this.#feedback(field);

    this.#control(field).classList.add('is-invalid');
    field.setAttribute('aria-invalid', 'true');
    feedback.textContent = message;
    feedback.classList.add('d-block');
    this.#describe(field, feedback);
  }

  #markValid (field) {
    this.#control(field).classList.remove('is-invalid');
    field.removeAttribute('aria-invalid');

    const feedback = this.#feedback(field, false);

    if (!feedback) return;

    feedback.textContent = '';
    feedback.classList.remove('d-block');
  }

  #describe (field, feedback) {
    const ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);

    if (ids.includes(feedback.id)) return;

    field.setAttribute('aria-describedby', [...ids, feedback.id].join(' '));
  }

  #control (field) {
    return field.tomselect ? field.tomselect.wrapper : field;
  }

  #feedback (field, create = true) {
    const control = this.#control(field);
    let feedback = control.parentElement.querySelector(':scope > .invalid-feedback');

    if (!feedback && create) {
      feedback = document.createElement('div');
      feedback.id = `${field.id}Feedback`;
      feedback.className = 'invalid-feedback';
      control.insertAdjacentElement('afterend', feedback);
    }

    return feedback;
  }
}
